import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { feeApi } from '../../api/services';
import toast from 'react-hot-toast';
import {
  X,
  Save,
  DollarSign,
  MessageSquare,
  BookOpen,
  Search,
  Send
} from 'lucide-react';

const FeeReminderForm = ({ reminder, selectedClasses = [], feeRecords = [], feeStructures = [], students: apiStudents = [], classes: apiClasses = [], onClose, onRefresh }) => {
  const { isAdmin, isTeacher } = useAuth(); // eslint-disable-line no-unused-vars
  const [formData, setFormData] = useState({
    studentName: '',
    studentId: '',
    className: '',
    feeType: '',
    amount: '',
    dueDate: '',
    status: 'pending',
    phoneNumber: '',
    parentPhone: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedFeeRecord, setSelectedFeeRecord] = useState(null);
  const [sending, setSending] = useState(false);

  // Get students with pending/overdue fee records
  const studentsWithPendingFees = useMemo(() => {
    if (!feeRecords || feeRecords.length === 0) return [];
    
    return apiStudents
      .map(student => {
        const feeRecord = feeRecords.find(record => {
          const recordStudentId = record.studentId?._id || record.studentId;
          return recordStudentId === student._id;
        });
        
        if (!feeRecord) return null;
        
        const hasOverdue = feeRecord.breakdown?.some(cat => 
          cat.status === 'Due' && cat.dueDate && new Date(cat.dueDate) < new Date()
        );
        const hasPartial = feeRecord.breakdown?.some(cat => cat.status === 'Partial');
        const status = feeRecord.balance === 0 ? 'paid' : hasOverdue ? 'overdue' : hasPartial ? 'partial' : 'pending';
        
        // Only include if not fully paid
        if (feeRecord.balance === 0) return null;
        
        return {
          student,
          feeRecord,
          status,
          balance: feeRecord.balance || 0,
          totalAmount: feeRecord.totalAmount || 0
        };
      })
      .filter(Boolean);
  }, [feeRecords, apiStudents]);

  // Filter students with pending fees based on search term
  const filteredStudents = studentsWithPendingFees.filter(({ student }) => {
    const matchesSearch = studentSearchTerm === '' || 
      `${student.firstName} ${student.lastName}`.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
      student.studentRegistrationId?.toLowerCase().includes(studentSearchTerm.toLowerCase());
    
    // Filter by selected classes if any
    if (selectedClasses.length > 0) {
      const studentClassId = student.enrollment?.unitId?._id || student.enrollment?.unitId || student.unitId?._id || student.unitId;
      const matchesClass = selectedClasses.some(cls => cls._id === studentClassId || cls.id === studentClassId);
      return matchesSearch && matchesClass;
    }
    
    return matchesSearch;
  }).slice(0, 50); // Limit to 50 results for performance

  useEffect(() => {
    if (reminder && reminder.studentObj && reminder.feeRecord) {
      const student = reminder.studentObj;
      const feeRecord = reminder.feeRecord;
      const classData = apiClasses.find(c => {
        const studentClassId = student?.enrollment?.unitId?._id || student?.enrollment?.unitId || student?.unitId?._id || student?.unitId;
        return c._id === studentClassId;
      });
      
      setSelectedStudent(student);
      setSelectedFeeRecord(feeRecord);
      setFormData({
        studentName: reminder.studentName || '',
        studentId: student._id || '',
        className: classData?.unitName || reminder.className || '',
        feeType: reminder.feeType || '',
        amount: reminder.amount || feeRecord.balance || '',
        dueDate: reminder.dueDate ? new Date(reminder.dueDate).toISOString().split('T')[0] : '',
        status: reminder.status || 'pending',
        phoneNumber: student?.studentPhone || reminder.phoneNumber || '',
        parentPhone: student?.parentPhone || reminder.parentPhone || '',
        notes: reminder.notes || ''
      });
    } else if (reminder) {
      // Fallback for old format
      setFormData({
        studentName: reminder.studentName || '',
        studentId: reminder.studentId || '',
        className: reminder.className || '',
        feeType: reminder.feeType || '',
        amount: reminder.amount || '',
        dueDate: reminder.dueDate ? new Date(reminder.dueDate).toISOString().split('T')[0] : '',
        status: reminder.status || 'pending',
        phoneNumber: reminder.phoneNumber || '',
        parentPhone: reminder.parentPhone || '',
        notes: reminder.notes || ''
      });
    }
  }, [reminder, apiClasses]);

  // Auto-populate class when classes are pre-selected
  useEffect(() => {
    if (selectedClasses.length === 1) {
      setFormData(prev => ({
        ...prev,
        className: selectedClasses[0].name
      }));
    }
  }, [selectedClasses]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Auto-fill student details when student ID is selected
    if (name === 'studentId') {
      const selectedStudent = apiStudents.find(s => s.id === value);
      if (selectedStudent) {
        setFormData(prev => ({
          ...prev,
          studentName: selectedStudent.name,
          className: selectedStudent.className,
          phoneNumber: selectedStudent.phone,
          parentPhone: selectedStudent.parentPhone
        }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.studentId) newErrors.studentId = 'Student selection is required';
    if (!selectedFeeRecord && !reminder) {
      newErrors.studentId = 'Please select a student with pending fees';
    }
    // Removed parent phone validation as backend might handle it or look it up
    // But keeping it for now as good practice if we were sending directly, 
    // but for API call, maybe not strictly required if backend has it.
    // However, let's keep it to ensure data integrity.
    if (!formData.parentPhone) {
       // newErrors.parentPhone = 'Parent phone number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    handleSendReminder();
  };

  const handleSendReminder = async () => {
    if (!selectedStudent) {
      toast.error("No student selected");
      return;
    }

    setSending(true);
    try {
        // Payload for Scenario C: Individual Reminder
        const payload = {
            recipientStudentIds: [selectedStudent._id],
            scheduleDate: new Date().toISOString(), // Current time for immediate send
            recipientType: localStorage.getItem('organizationType')?.toUpperCase() === 'COLLEGE' ? 'both' : 'parent',
            sendNow: true
        };

        const res = await feeApi.createReminderCampaign(payload);

        if (res.success) {
            toast.success('Reminder sent successfully!');
            if (onRefresh) onRefresh();
            onClose();
        } else {
            toast.error(res.message || 'Failed to send reminder');
        }
    } catch (error) {
        toast.error('Failed to send reminder');
    } finally {
        setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-1.5 bg-primary-100 rounded">
              <DollarSign className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {reminder ? 'Send Fee Reminder' : 'New Fee Reminder'}
              </h2>
              <p className="text-xs text-gray-600">
                Send an individual fee reminder to this student.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Selected Classes */}
        {selectedClasses.length > 0 && (
          <div className="px-4 py-3 bg-primary-50 border-b border-primary-200">
            <div className="flex items-center space-x-2 mb-2">
              <BookOpen className="h-3.5 w-3.5 text-primary-600" />
              <span className="text-xs font-medium text-primary-900">
                Selected Classes ({selectedClasses.length})
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {selectedClasses.map((cls) => (
                <span key={cls.id} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary-100 text-primary-800">
                  {cls.name} ({cls.studentCount} students)
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Student Selection */}
          <div className="space-y-3">
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Only show class selection if no classes were pre-selected */}
              {selectedClasses.length === 0 && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Class *
                  </label>
                  <select
                    name="className"
                    value={formData.className}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  >
                    <option value="">Choose a class</option>
                    {apiClasses.map(cls => (
                      <option key={cls._id} value={cls.unitName}>{cls.unitName}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Student *
                </label>
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    <input
                      type="text"
                      value={studentSearchTerm}
                      onChange={(e) => {
                        setStudentSearchTerm(e.target.value);
                        setShowStudentDropdown(true);
                      }}
                      onFocus={() => setShowStudentDropdown(true)}
                      className={`w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 ${errors.studentId ? 'border-red-500' : ''}`}
                      placeholder="Search..."
                    />
                  </div>
                  
                  {/* Selected Student Display */}
                  {selectedStudent && selectedFeeRecord && (
                    <div className="mt-2 p-2 bg-primary-50 border border-primary-200 rounded">
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex-1">
                          <div className="font-medium text-primary-900 text-sm">
                            {selectedStudent.firstName} {selectedStudent.lastName}
                          </div>
                          <div className="text-xs text-primary-600">
                            {selectedStudent.studentRegistrationId} • {formData.className}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedStudent(null);
                            setSelectedFeeRecord(null);
                            setFormData(prev => ({ 
                              ...prev, 
                              studentId: '', 
                              studentName: '',
                              className: '',
                              amount: '',
                              phoneNumber: '',
                              parentPhone: ''
                            }));
                          }}
                          className="text-primary-600 hover:text-primary-800"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-[10px]">
                        <div>
                          <span className="text-gray-600">Balance Due:</span>
                          <div className="font-semibold text-red-600">₹{selectedFeeRecord.balance?.toLocaleString() || '0'}</div>
                        </div>
                        <div>
                          <span className="text-gray-600">Total Amount:</span>
                          <div className="font-medium text-gray-900">₹{selectedFeeRecord.totalAmount?.toLocaleString() || '0'}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Student Dropdown */}
                  {showStudentDropdown && studentSearchTerm && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-48 overflow-y-auto">
                      {filteredStudents.length > 0 ? (
                        filteredStudents.map(studentData => {
                          const student = studentData.student;
                          const classData = apiClasses.find(c => {
                            const studentClassId = student?.enrollment?.unitId?._id || student?.enrollment?.unitId || student?.unitId?._id || student?.unitId;
                            return c._id === studentClassId;
                          });
                          return (
                            <button
                              key={student._id}
                              type="button"
                              onClick={() => {
                                setSelectedStudent(student);
                                setSelectedFeeRecord(studentData.feeRecord);
                                setFormData(prev => ({
                                  ...prev,
                                  studentId: student._id,
                                  studentName: `${student.firstName} ${student.lastName}`,
                                  className: classData?.unitName || '',
                                  amount: studentData.balance,
                                  phoneNumber: student.studentPhone || '',
                                  parentPhone: student.parentPhone || '',
                                  feeType: studentData.feeRecord.academicYear || 'Fee Reminder'
                                }));
                                setStudentSearchTerm('');
                                setShowStudentDropdown(false);
                              }}
                              className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900 text-xs">
                                    {student.firstName} {student.lastName}
                                  </div>
                                  <div className="text-[10px] text-gray-500">
                                    {student.studentRegistrationId} • {classData?.unitName || 'Unknown Class'}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-xs font-semibold text-red-600">
                                    ₹{studentData.balance.toLocaleString()}
                                  </div>
                                  <div className="text-[10px] text-gray-500">
                                    {studentData.status === 'overdue' ? 'Overdue' : 'Pending'}
                                  </div>
                                </div>
                              </div>
                            </button>
                          );
                        })
                      ) : (
                        <div className="px-3 py-2 text-gray-500 text-xs">
                          {feeRecords.length === 0 
                            ? 'No fee records found. Please assign fee structures to students first.'
                            : 'No students with pending fees found matching your search.'
                          }
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {errors.studentId && (
                  <p className="text-red-500 text-xs mt-1">{errors.studentId}</p>
                )}
              </div>
            </div>
          </div>

          {/* Fee Details - Auto-filled from fee record */}
          {selectedFeeRecord && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Academic Year / Fee Structure
                </label>
                <input
                  type="text"
                  name="feeType"
                  value={formData.feeType || selectedFeeRecord.academicYear || 'Fee Record'}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded bg-gray-50"
                  readOnly
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Balance Due (₹)
                </label>
                <input
                  type="text"
                  name="amount"
                  value={selectedFeeRecord.balance || formData.amount || '0'}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded bg-gray-50 font-semibold text-red-600"
                  readOnly
                />
              </div>
            </div>
          )}

          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Student Phone Number
              </label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 text-sm border border-gray-300 rounded ${errors.phoneNumber ? 'border-red-500' : ''}`}
                placeholder="+91-98765-43210"
                readOnly
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Parent Phone Number
              </label>
              <input
                type="tel"
                name="parentPhone"
                value={formData.parentPhone}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 text-sm border border-gray-300 rounded ${errors.parentPhone ? 'border-red-500' : ''}`}
                placeholder="+91-98765-43210"
                readOnly
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="pt-4 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-3 sm:space-y-0">
              <div className="flex items-center space-x-3">
                {/* Left side actions if any */}
              </div>

              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3 py-1.5 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50"
                  disabled={sending}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSendReminder}
                  className="btn-primary text-sm flex items-center space-x-1.5"
                  disabled={!formData.studentId || sending}
                >
                  {sending ? (
                    <>
                        <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                        <span>Sending...</span>
                    </>
                  ) : (
                    <>
                        <Send className="h-3.5 w-3.5" />
                        <span>Send Reminder</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeeReminderForm;
